import { CalendarEvent } from "calendar-link";
import User from "./user";
import PublicTable from "./public-table";
import PublicApiForm from "./public-api-form";
import Application from "./application";
import Api from "./api";
import IDocument from "../interface/document";
import IApiTokenVerification from "../interface/api-token-verification";
import IApiTokenQuestion from "../interface/api-token-question";
import IApiTokenPublicForm from "../interface/api-token-public-form";
import IApiTokenGdpr from "../interface/api-token-gdpr";
import Global from "../global";
import ServiceName from "../enum/service-name";
import PublicApiService from "../enum/public-api-service";
import PublicApiCommonService from "../enum/public-api-common-service";
import LogicalValue from "../enum/logical-value";
import GdprStatus from "../enum/gdpr-status";
import ErrorCode from "../enum/error-code";

class PublicApiSingleton {

    /* Public Methods */

    public getGdprAddress(email: string, status: GdprStatus): string {
        const token: string = btoa(JSON.stringify({
            i: btoa(User.getInstance()),
            a: btoa(Application.getId()),
            e: btoa(email),
            v: status
        } as IApiTokenGdpr));
        return this.getCommonServiceAddress(PublicApiCommonService.Gdpr, token);
    }

    public getQuestionAddress(documentId: number, fieldId: number, value: LogicalValue): string {
        const token: string = btoa(JSON.stringify({
            i: btoa(User.getInstance()),
            a: btoa(Application.getId()),
            r: btoa(documentId.toString()),
            f: btoa(fieldId.toString()),
            v: btoa(value)
        } as IApiTokenQuestion));
        return this.getCommonServiceAddress(PublicApiCommonService.Question, token);
    }

    public getVerificationAddress(address: string, service: ServiceName): string {
        const token: string = btoa(JSON.stringify({
            i: btoa(User.getInstance()),
            a: btoa(Application.getId()),
            e: btoa(address),
            s: btoa(service)
        } as IApiTokenVerification));
        return this.getCommonServiceAddress(PublicApiCommonService.Verification, token);
    }

    public getIcsAddress(event: CalendarEvent): string {
        const token: string = encodeURIComponent(btoa(JSON.stringify(event)));
        return this.getCommonServiceAddress(PublicApiCommonService.Ics, token);
    }

    public getFormAddress(formId: number, docId: number = PublicApiForm.DefaultRecordId): string {
        const data: IApiTokenPublicForm = {
            i: btoa(User.getInstance()),
            a: btoa(Application.getId()),
            f: btoa(formId.toString()),
            r: btoa(docId.toString())
        };
        const token: string = btoa(JSON.stringify(data));
        return `${this.getNewAddress()}/${PublicApiService.Form}/?f=${token}`;
    }

    public getTableAddress(tableId: number, entityId: number, doc: IDocument): string {
        const ownerId: number = PublicTable.getOwnerId(tableId, entityId, doc) || 0;
        const token: string = btoa(JSON.stringify({
            i: btoa(User.getInstance()),
            a: btoa(Application.getId()),
            t: btoa(tableId.toString()),
            r: btoa(ownerId.toString())
        }));
        return `${this.getOldAddress()}/publicaccess/?pa=${token}`;
    }

    public async getQuestionAddressShorten(documentId: number, fieldId: number, value: LogicalValue): Promise<string> {
        return await this.getShorten(this.getQuestionAddress(documentId, fieldId, value));
    }

    public async getVerificationAddressShorten(address: string, service: ServiceName): Promise<string> {
        return await this.getShorten(this.getVerificationAddress(address, service));
    }

    /* Private Methods */

    public getOldAddress(): string {
        // TODO - Delete this after Public Table migration
        return Api.getPublicApiUrl();
    }

    public getNewAddress(): string {
        // TODO - Delete this after Public Table migration, replace with .getAddress
        return Api.getPublicApiUrl().replace('flpublicapi', 'pubapi').replace('/preprod', '');
    }

    private getCommonServiceAddress(service: PublicApiCommonService, token: string) {
        return `${this.getNewAddress()}/${PublicApiService.Common}/${service}/${token}`;
    }

    private async getShorten(longUrl: string): Promise<string> {
        try {
            const response: Response = await fetch(Global.BITLY_ADDRESS, {
                method: 'POST',
                headers: { 'content-type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ access_token: Global.BITLY_TOKEN, longUrl }),
            });
            if (response.ok) {
                const json: any = await response.json();
                if (json.status_code === 200) {
                    return json.data.url;
                }
            }
            throw ErrorCode.BitlyRequestFailed;
        } catch (e) {
            throw ErrorCode.BitlyRequestFailed;
        }
    }

}

const PublicApi = new PublicApiSingleton();

export default PublicApi;